import React from 'react';
import { Card, Row, Col, Statistic, Tag, Space, Typography, Tooltip } from 'antd';
import {
  AmazonOutlined,
  ShoppingOutlined,
  CalendarOutlined,
  DollarOutlined,
  TruckOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

const OrderSummary = ({ order }) => {
  const getOrderStatusTag = (status) => {
    const statusMap = {
      'Unshipped': { color: 'orange', icon: <ClockCircleOutlined /> },
      'PartiallyShipped': { color: 'blue', icon: <TruckOutlined /> },
      'Shipped': { color: 'green', icon: <CheckCircleOutlined /> },
      'Canceled': { color: 'red', icon: <ClockCircleOutlined /> }
    };
    
    const config = statusMap[status] || { color: 'default', icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {status}
      </Tag>
    );
  };

  const getFulfillmentChannelTag = (channel) => {
    return channel === 'AFN' ? (
      <Tag color="orange">AFN (Amazon)</Tag>
    ) : (
      <Tag color="blue">MFN (Merchant)</Tag>
    );
  };

  // Calculate total items
  const totalItems = order.orderItems?.reduce((sum, item) => sum + (item.quantityOrdered || 0), 0) || 0;
  
  // Calculate total earnings
  const totalEarnings = order.orderItems?.reduce((sum, item) => sum + (parseFloat(item.itemEarnings || 0)), 0) || 0;

  return (
    <Card className="order-summary-card" style={{ marginBottom: 24 }}>
      <Row align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="center">
            <AmazonOutlined style={{ fontSize: 24, color: '#FF9900' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Order {order.amazonOrderId}
              </Title>
              <Text type="secondary">
                {order.sellerAccount?.name} • {order.marketplace}
              </Text>
            </div>
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Statistic
            title="Order Status"
            value={order.orderStatus}
            formatter={() => getOrderStatusTag(order.orderStatus)}
          />
        </Col>
        
        <Col xs={12} sm={6}>
          <Statistic
            title="Fulfillment"
            value={order.fulfillmentChannel}
            formatter={() => getFulfillmentChannelTag(order.fulfillmentChannel)}
          />
        </Col>
        
        <Col xs={12} sm={6}>
          <Statistic
            title="Total Items"
            value={totalItems}
            prefix={<ShoppingOutlined />}
          />
        </Col>
        
        <Col xs={12} sm={6}>
          <Statistic
            title="Order Total"
            value={order.orderTotalAmount || 0}
            precision={2}
            prefix={<DollarOutlined />}
          />
        </Col>
        
        <Col xs={12} sm={6}>
          <Statistic
            title="Total Earnings"
            value={totalEarnings}
            precision={2}
            prefix={<DollarOutlined />}
            valueStyle={{ color: '#52c41a' }}
          />
        </Col>
        
        <Col xs={12} sm={6}>
          <Statistic
            title="Purchase Date"
            value={order.purchaseDate ? dayjs(order.purchaseDate).format('MMM DD, YYYY') : '-'}
            prefix={<CalendarOutlined />}
          />
        </Col>
        
        <Col xs={12} sm={6}>
          <Statistic
            title="Ship Service"
            value={order.shipServiceLevel === 'Expedited' ? 'Expedited' : 'Standard'}
            formatter={(value) => (
              <Tag color={value === 'Expedited' ? 'blue' : 'default'}>
                {value}
              </Tag>
            )}
          />
        </Col>
        
        <Col xs={12} sm={6}>
          <Statistic
            title="Last Update"
            value={order.lastUpdateDate ? dayjs(order.lastUpdateDate).format('MMM DD, HH:mm') : '-'}
          />
        </Col>
      </Row>
    </Card>
  );
};

export default OrderSummary;
